// src/components/CampaignAssignmentModal.js
// Modal for manually assigning DealerApps campaigns to orders

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/CampaignAssignmentModal.css';

function CampaignAssignmentModal({ isOpen, onClose, analyticsRecord }) {
  const [orders, setOrders] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

useEffect(() => {
  if (isOpen && analyticsRecord) {
    const loadOrders = async () => {
      setLoading(true);
      try {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('clientId', '==', analyticsRecord.clientId),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(ordersQuery);
        const ordersList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setOrders(ordersList);
      } catch (error) {
        console.error('Error loading orders:', error);
        alert('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
    setAssignments(analyticsRecord.assignments || {});
  }
}, [isOpen, analyticsRecord]);



  const handleAssignment = (campaignTitle, orderId) => {
    setAssignments(prev => ({
      ...prev,
      [campaignTitle]: orderId || null
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update the analytics document with assignments
      await updateDoc(doc(db, 'analytics', analyticsRecord.id), {
        assignments
      });

      // Update each assigned order with the latest analytics
      for (const [campaignTitle, orderId] of Object.entries(assignments)) {
        if (!orderId) continue; // Skip unassigned campaigns

        const campaign = analyticsRecord.campaigns.find(c => c.title === campaignTitle);
        if (!campaign) continue;

        await updateDoc(doc(db, 'orders', orderId), {
          latestAnalytics: {
            campaignTitle,
            invitations: campaign.invitations,
            responses: campaign.responses,
            appointments: campaign.appointments,
            responseRate: campaign.responseRate,
            saleDates: campaign.saleDates,
            leadUrl: campaign.leadUrl,
            reportUrl: campaign.reportUrl,
            lastUpdated: new Date(),
            analyticsRecordId: analyticsRecord.id
          }
        });
      }

      alert('Assignments saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="campaign-assignment-overlay">
      <div className="campaign-assignment-modal">
        <div className="modal-header">
          <h2>Assign Campaigns to Orders</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <div className="modal-subtitle">
          <strong>{analyticsRecord.clientName}</strong> - Scraped {analyticsRecord.scrapedAt?.toLocaleDateString()}
        </div>

        {loading ? (
          <div className="modal-loading">Loading orders...</div>
        ) : (
          <div className="modal-content">
            <div className="assignment-grid">
              <div className="grid-header">
                <div>Campaign from DealerApps</div>
                <div>Metrics</div>
                <div>Assign to Order</div>
              </div>

              {analyticsRecord.campaigns.map((campaign, index) => (
                <div key={index} className="assignment-row">
                  <div className="campaign-info">
                    <div className="campaign-title">{campaign.title}</div>
                    {campaign.saleDates && campaign.saleDates.length > 0 && (
                      <div className="campaign-dates">
                        Sale Dates: {campaign.saleDates.join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="campaign-metrics">
                    <span className="metric">
                      <strong>{campaign.invitations}</strong> inv
                    </span>
                    <span className="metric">
                      <strong>{campaign.responses}</strong> resp
                    </span>
                    <span className="metric">
                      <strong>{campaign.appointments}</strong> appt
                    </span>
                    {campaign.responseRate && (
                      <span className="metric">
                        <strong>{campaign.responseRate}</strong>
                      </span>
                    )}
                  </div>

                  <div className="campaign-assignment">
                    <select
                      value={assignments[campaign.title] || ''}
                      onChange={(e) => handleAssignment(campaign.title, e.target.value)}
                      className="order-select"
                    >
                      <option value="">-- Not Assigned --</option>
                      {orders.map(order => (
                        <option key={order.id} value={order.id}>
                          {order.jobNumber} - {order.campaignName || 'Unnamed Campaign'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {analyticsRecord.campaigns.length === 0 && (
              <div className="no-campaigns">
                No campaigns found in this analytics record.
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn" disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignAssignmentModal;